import type React from "react"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { useAuth, AuthProvider } from "../contexts/auth-context"

// Public pages
import AboutPage from "./pages/public/AboutPage"
import HelpPage from "./pages/public/HelpPage"
import LoginPage from "./pages/public/LoginPage"
import ForgotPasswordPage from "./pages/public/ForgotPassword";
import ResetPasswordPage from "./pages/public/ResetPassword";


// User pages
// import DashboardPage from "./pages/user/DashboardPage"
import QuotaPage from "./pages/user/QuotaPage"
import ReportPage from "./pages/user/ReportsPage"
import ReportDetailsPage from "./pages/user/ReportDetailPage"
import ReportProcessingPage from "./pages/user/ReportProcessingPage"

// import CreateReportPage from "./pages/user/CreateReportPage"

import BulkReportProcessingPage from "./pages/user/BulkReportProcessingPage";
import SupportPage from "./pages/user/SupportPage";
import PolicySearchPage from "./pages/user/PolicySearchPage";
import ChangePasswordPage from "./pages/user/ChangePasswordPage";

import { ProtectedRoute } from "../components/protected-route"
import DashboardFlow from "./pages/user/DashboardFlow"
import PeerGroupAnalysisPage from "./pages/user/PeerGroupAnalysisPage"
import CountryMemoPage from "./pages/user/CountryMemoPage"

interface PrivateRouteProps {
  children: React.ReactNode
}

function PrivateRoute({ children }: PrivateRouteProps) {
  const { user } = useAuth()
  if (!user) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function AppRoutes() {
  const { user } = useAuth()

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected user routes (accessible to all authenticated users) */}
      <Route
        path="/user/*"
        element={
          <ProtectedRoute requiredRole={["User", "PolicyUser", "Admin"]}>
            <PrivateRoute>
              <Routes>
                <Route path="dashboard" element={<DashboardFlow />} />
                <Route path="quota" element={<QuotaPage />} />
                <Route path="reports" element={<ReportPage />} />
                <Route path="peer-group-analysis" element={<PeerGroupAnalysisPage />} />
                <Route path="country-memo" element={<CountryMemoPage />} />
                <Route path="reports/:id" element={<ReportDetailsPage />} />
                <Route path="reports/:id/processing" element={<ReportProcessingPage />} />
                {/* <Route path="create-report" element={<CreateReportPage />} /> */}
                <Route path="reports/bulk-processing" element={<BulkReportProcessingPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="policy-search" element={<PolicySearchPage />} />
                <Route path="change-password" element={<ChangePasswordPage />} />
                <Route path="*" element={<Navigate to="dashboard" replace />} />
              </Routes>
            </PrivateRoute>
          </ProtectedRoute>
        }
      />

      {/* Default root → login */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Redirect after login — everyone goes to the user dashboard */}
      {user && <Route path="/" element={<Navigate to="/user/dashboard" replace />} />}
      {user?.role === "PolicyUser" && <Route path="/" element={<Navigate to="/user/policy-search" replace />} />}
    </Routes>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  )
}
